<template>
  <div v-if="!tenantReady" class="app-loading" :class="{ 'app-loading--work': isWorkRoute }">
    <template v-if="isWorkRoute">
      <div class="wl-ribbon" />
      <div class="wl-body">
        <div class="wl-image" />
        <div class="wl-panel" />
      </div>
    </template>
    <span v-else class="app-spinner" />
  </div>
  <div v-else id="app" :class="appClasses">
    <VisitorNavbar v-if="!platformMode && !isAdminLayout" />
    <AdminSidebar v-else-if="isAdminLayout" />
    <PlatformNavbar v-else />
    <main class="main-content">
      <router-view />
    </main>
    <VisitorFooter v-if="!platformMode && !isAdminLayout" />
    <PlatformFooter v-else-if="!isAdminLayout" />
    <router-view name="modal" />
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue'
import VisitorNavbar from '@/components/VisitorNavbar.vue'
import VisitorFooter from '@/components/Footer.vue'
import PlatformNavbar from '@/components/PlatformNavbar.vue'
import PlatformFooter from '@/components/PlatformFooter.vue'
// Admin-only: loaded lazily so it (and firebase/auth via its logout) stays
// out of the visitor critical-path bundle.
const AdminSidebar = defineAsyncComponent(() => import('@/components/AdminSidebar.vue'))
import { store, actions } from '@/store'
import { tenantReadyPromise, isTenantHost, hostnameHasTenant } from '@/utils/tenantUtils'
import { applyTheme, clearRootTheme, prePaintTheme } from '@/utils/applyTheme'
import { mark } from '@/utils/perfMarks'
import '@/styles/global.css'

export default {
  name: 'App',
  components: {
    AdminSidebar,
    VisitorNavbar,
    VisitorFooter,
    PlatformNavbar,
    PlatformFooter
  },
  data() {
    return {
      platformMode: !hostnameHasTenant(),
      tenantReady: false,
      isWorkRoute: /\/gallery\//.test(window.location.pathname),
    }
  },
  computed: {
    isAdminLayout() {
      const path = this.$route?.name ? this.$route.path : window.location.pathname
      // Admin chrome requires both the /admin path AND a verified admin session - never
      // render the sidebar from the URL alone.
      return path.startsWith('/admin') && store.user.isAdmin
    },
    isAuthRoute() {
      const name = this.$route?.name
      return name === 'login' || name === 'signup'
    },
    artistTheme() {
      return store.artistConfig?.theme
    },
    artistIconUrl() {
      return store.artistConfig?.theme?.iconUrl
    },
    appClasses() {
      return {
        'layout-admin': this.isAdminLayout,
        'platform-mode': this.platformMode && !this.isAdminLayout,
        'visitor-theme': !this.isAdminLayout && !this.isAuthRoute && !this.platformMode,
        'page-content-shown': store.pageContentShown
      }
    }
  },
  watch: {
    artistTheme: {
      handler: 'applyOrScopeTheme',
      immediate: true,
    },
    artistIconUrl: {
      handler(url) {
        if (this.platformMode) return
        const link = document.querySelector('link[rel="icon"]')
        if (link) link.href = url || `${process.env.BASE_URL}icon-default.svg`
      },
      immediate: true,
    },
    isAdminLayout: 'applyOrScopeTheme',
    isAuthRoute: 'applyOrScopeTheme',
    '$route.name': 'applyOrScopeTheme',
    tenantReady(newVal) {
      if (newVal) this.applyOrScopeTheme()
    }
  },
  async created() {
    mark('app:created')
    mark('app:tenant:start')
    // Apply cached theme immediately (store pre-populates from localStorage before this runs)
    if (!this.platformMode && store.artistConfig?.theme) {
      prePaintTheme(store.artistConfig.theme)
    }
    await tenantReadyPromise
    mark('app:tenant:end')
    this.platformMode = !isTenantHost()
    // Reveal the app immediately using cached data — Firestore loads run in background
    // and update the store reactively (artistTheme watcher re-applies theme when ready)
    this.tenantReady = true
    mark('app:ready')
    if (!this.platformMode) {
      actions.loadArtistConfig()
      actions.loadCustomPages({ navOnly: true })
      actions.initializeWorks()
    }
  },
  methods: {
    applyOrScopeTheme() {
      if (this.isAdminLayout || this.isAuthRoute || this.platformMode) {
        clearRootTheme()
        return
      }
      const theme = this.artistTheme
      if (!theme) return
      prePaintTheme(theme)
      this.$nextTick(() => applyTheme(theme))
    }
  }
}
</script>

<style>
@import '@/styles/theme.css';

#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  min-height: 100vh;
  /* Visible height on phones, where 100vh includes the browser toolbar */
  min-height: 100dvh;
  margin: 0;
  display: grid;
  grid-template-rows: auto 1fr auto;
  grid-template-columns: minmax(0, 1fr);
}

#app.visitor-theme {
  background-color: var(--color-bg);
  color: var(--color-text);
}

/* Show the footer only with page content or an error, so it can't ride up while a page loads; previews excluded */
#app:not(.page-content-shown) #outerContainerFooter:where(:not(.preview-page *)) {
  visibility: hidden;
}

#app.platform-mode {
  background: var(--color-admin-bg);
  color: var(--color-admin-text);
}

#app.layout-admin {
  grid-template-columns: var(--admin-sidebar-width, 220px) 1fr;
  grid-template-rows: 1fr;
  text-align: left;
  transition: grid-template-columns 0.25s ease;
}

#app.layout-admin .main-content {
  min-width: 0;
  overflow-x: clip;
  overflow-y: auto;
}

@media (max-width: 768px) {
  #app.layout-admin {
    grid-template-columns: 1fr;
    padding-top: 56px;
  }
}

.app-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
}

/* Work-route loading skeleton — mimics the modal layout */
.app-loading--work {
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  background-color: var(--color-bg, #f5f5f3);
}

.wl-ribbon {
  height: 56px;
  background-color: var(--color-header, #2c2c2c);
  flex-shrink: 0;
}

.wl-body {
  flex: 1;
  display: flex;
  min-height: 0;
}

.wl-image {
  flex: 1;
  position: relative;
  background-color: var(--color-header, #2c2c2c);
  overflow: hidden;
}

.wl-image::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.06) 50%, transparent 100%);
  animation: wl-shimmer 2.2s ease-in-out infinite;
}

.wl-panel {
  display: none;
  background-color: var(--color-header, #2c2c2c);
  border-left: 1px solid rgba(255, 255, 255, 0.08);
}

@media (min-width: 768px) {
  .wl-panel {
    display: block;
    width: 30%;
    min-width: 280px;
  }
}

@keyframes wl-shimmer {
  0% { left: -100%; }
  100% { left: 100%; }
}

.app-spinner {
  display: inline-block;
  width: 32px;
  height: 32px;
  border: 3px solid rgba(128, 128, 128, 0.2);
  border-top-color: rgba(128, 128, 128, 0.6);
  border-radius: 50%;
  animation: app-spin 0.7s linear infinite;
}

@keyframes app-spin {
  to { transform: rotate(360deg); }
}
</style>
